import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiClientError } from "../api.js";
import type { Client, ClientInput } from "../types.js";
import { displayHostname } from "../url-display.js";

const emptyClient: ClientInput = {
  name: "",
  website: "",
  industry: "",
  description: "",
  positioning: "",
  targetAudience: "",
  communicationTone: "",
};

export function ClientsScreen() {
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        search,
        status,
        page: "1",
        pageSize: "50",
        sort: "updated_at",
        order: "desc",
      });
      const result = await api.listClients(query);
      setClients(result.items);
      setTotal(result.total);
      setError("");
    } catch {
      setError("Не удалось загрузить клиентов.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const timeout = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(timeout);
  }, [search, status]);

  return (
    <section className="panel clients-panel">
      <div className="page-heading">
        <div>
          <h1>Клиенты</h1>
          <p>База клиентов и материалов бренда · {total} записей</p>
        </div>
        <button
          className="primary compact-button"
          onClick={() => setShowCreate(true)}
        >
          ＋ Новый клиент
        </button>
      </div>
      <div className="table-toolbar">
        <label className="search-field">
          ⌕
          <input
            aria-label="Поиск клиентов"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Название, сайт или отрасль"
          />
        </label>
        <select
          aria-label="Статус клиентов"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="active">Активные</option>
          <option value="archived">Архивные</option>
          <option value="all">Все статусы</option>
        </select>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      <div className="client-table" role="table">
        <div className="client-row table-head" role="row">
          <span>Клиент</span>
          <span>Сайт</span>
          <span>Отрасль</span>
          <span>Статус</span>
          <span>Файлы</span>
          <span>Обновлён</span>
          <span />
        </div>
        {loading ? (
          <div className="table-message">Загрузка…</div>
        ) : clients.length === 0 ? (
          <div className="table-message">Клиенты не найдены</div>
        ) : (
          clients.map((client) => (
            <ClientRow
              key={client.id}
              client={client}
              onChanged={() => void load()}
            />
          ))
        )}
      </div>
      {showCreate ? (
        <CreateClientDialog onClose={() => setShowCreate(false)} />
      ) : null}
    </section>
  );
}

function ClientRow({
  client,
  onChanged,
}: {
  client: Client;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const open = () => void navigate(`/clients/${client.id}`);
  return (
    <div
      className="client-row clickable-row"
      role="row"
      tabIndex={0}
      onClick={(event) => {
        if (!(event.target as HTMLElement).closest("a, button")) open();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") open();
      }}
    >
      <span className="client-name">
        <i>{client.name.slice(0, 2).toUpperCase()}</i>
        <Link to={`/clients/${client.id}`}>{client.name}</Link>
      </span>
      <span>
        {client.website ? (
          <a href={client.website} target="_blank" rel="noreferrer">
            {displayHostname(client.website)}
          </a>
        ) : (
          "—"
        )}
      </span>
      <span>{client.industry || "—"}</span>
      <span>
        <b className={`status-pill ${client.status}`}>
          {client.status === "active" ? "Активен" : "Архив"}
        </b>
      </span>
      <span>{client.assetCount}</span>
      <span>{formatDate(client.updatedAt)}</span>
      <span className="row-actions">
        <Link
          aria-label={`Открыть ${client.name}`}
          to={`/clients/${client.id}`}
        >
          →
        </Link>
        {client.status === "active" ? (
          <button
            aria-label={`Архивировать ${client.name}`}
            onClick={() => {
              if (
                window.confirm(
                  `Переместить клиента «${client.name}» в архив? Его можно будет восстановить через фильтр «Архивные».`,
                )
              )
                void api.archiveClient(client.id).then(onChanged);
            }}
          >
            ⋯
          </button>
        ) : (
          <button
            aria-label={`Восстановить ${client.name}`}
            onClick={() => void api.restoreClient(client.id).then(onChanged)}
          >
            Восстановить
          </button>
        )}
      </span>
    </div>
  );
}

function CreateClientDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState<ClientInput>(emptyClient);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (field: keyof ClientInput, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const client = await api.createClient(form);
      void navigate(`/clients/${client.id}`);
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : "Не удалось создать клиента.",
      );
      setSaving(false);
    }
  };
  return (
    <div className="dialog-backdrop" role="presentation">
      <form
        className="dialog"
        aria-label="Новый клиент"
        onSubmit={(event) => void submit(event)}
      >
        <div className="dialog-heading">
          <div>
            <h2>Создать клиента</h2>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="form-grid">
          <label>
            Название
            <input
              aria-label="Название клиента"
              value={form.name}
              onChange={(event) => update("name", event.target.value)}
              required
            />
          </label>
          <label>
            Сайт
            <input
              aria-label="Сайт"
              type="url"
              value={form.website ?? ""}
              onChange={(event) => update("website", event.target.value)}
              placeholder="https://"
            />
          </label>
          <label>
            Отрасль
            <input
              aria-label="Отрасль"
              value={form.industry ?? ""}
              onChange={(event) => update("industry", event.target.value)}
            />
          </label>
          <label className="wide">
            Описание
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
            />
          </label>
          <label className="wide">
            Позиционирование
            <textarea
              rows={3}
              value={form.positioning}
              onChange={(event) => update("positioning", event.target.value)}
            />
          </label>
          <label className="wide">
            Целевая аудитория
            <textarea
              rows={3}
              value={form.targetAudience}
              onChange={(event) => update("targetAudience", event.target.value)}
            />
          </label>
          <label className="wide">
            Тон коммуникации
            <textarea
              rows={3}
              value={form.communicationTone}
              onChange={(event) =>
                update("communicationTone", event.target.value)
              }
            />
          </label>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button className="primary compact-button" disabled={saving}>
            {saving ? "Создаём…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}
